/** 进页自动弹层类型，优先级从高到低（昨日领航者 / 待领取奖励不进队列，见 design §11） */
export enum EntryOverlayType {
  Guide = 'guide',
  PendingInvite = 'pending_invite',
}

/** 进页弹层管理顶层状态 */
export enum EntryOverlayPhase {
  Loading = 'loading',
  Idle = 'idle',
  Queueing = 'queueing',
  Showing = 'showing',
  Manual = 'manual',
}

/** 待处理邀请弹层形态：单条弹窗 vs 多条半屏 */
export type PendingInviteOverlayMode = 'single' | 'list'

export interface EntryOverlayItemBase {
  type: EntryOverlayType
}

export interface GuideOverlayItem extends EntryOverlayItemBase {
  type: EntryOverlayType.Guide
}

export interface PendingInviteOverlayItem extends EntryOverlayItemBase {
  type: EntryOverlayType.PendingInvite
  mode: PendingInviteOverlayMode
  invite?: HeartVoyagePendingInviteItem
  invites?: HeartVoyagePendingInviteItem[]
}

export type EntryOverlayItem = GuideOverlayItem | PendingInviteOverlayItem

/** 关系来源 relation_source（api README §2.3） */
export enum RelationSource {
  Platform = 1,
  Limited = 2,
}

/** 关系类型 relation_type / rel_type（api README §2.3） */
export enum RelationType {
  Couple = 1,
  Confidant = 2,
  CloseFriend = 3,
  Brother = 4,
  Sister = 5,
}

/** 待处理邀请 status（api README §2.3） */
export enum PendingInviteStatus {
  Inviting = 1,
  Accepted = 2,
  Rejected = 3,
  Expired = 4,
}

/** 可邀请状态 invite_status（api README §2.3） */
export enum InviteStatus {
  Invitable = 0,
  Inviting = 1,
  Uninvitable = 2,
  Bound = 3,
}

/** 邀请处理 action（invite_handle） */
export enum InviteHandleAction {
  Accept = 1,
  Decline = 2,
}

/** 用户资料 ActivityMemberV2 */
export interface ActivityMemberV2 {
  id: number
  name: string
  zy_id: string
  avatar: string
  gender: number
}

/** 双人榜条目 / 首页昨日头图 header_pair */
export interface HeartVoyageRankPairItem {
  rank: number
  score: number
  pair_key: string
  ship_level: number
  ship_form: number
  rel_type: RelationType
  member_a: ActivityMemberV2 | null
  member_b: ActivityMemberV2 | null
}

/** 决赛组简要 HeartVoyageGroupBrief */
export interface HeartVoyageGroupBrief {
  group_id: number
  group_name: string
}

/** PeakRacePanel 消费的首页字段（heart_voyage_homepage） */
export interface HeartVoyageHomepagePeakFields {
  schedule_segment: number
  final_groups: HeartVoyageGroupBrief[]
  /** 当前 pair 所在决赛组；仅决赛赛程窗内且已分组时 >0，否则 0 */
  current_final_group_id: number
}

/** 巅峰赛榜单 data（heart_voyage_peak_rank） */
export interface HeartVoyagePeakRankData {
  err_msg: string
  segment: number
  group_id: number
  group_name: string
  segment_countdown: number
  list: HeartVoyageRankPairItem[]
  own: HeartVoyageRankPairItem | null
  predict_group: HeartVoyageGroupBrief | null
}

/** 待处理邀请条目 HeartVoyagePendingInviteItem */
export interface HeartVoyagePendingInviteItem {
  invite_id: string
  rel_type: RelationType
  status: PendingInviteStatus
  expire_at: number
  peer: ActivityMemberV2 | null
}

/**
 * 首页聚合 — 进页弹层相关字段（heart_voyage_homepage）
 * 待领取奖励不在首页：可领看 stage_info.action_status，领取结果走 stage_claim.rewards
 */
export interface HomepageOverlayFlags {
  /** true = 需要新手引导（与需求草案 is_guide_finished 语义相反） */
  need_newbie_guide: boolean
  /**
   * 新增待处理邀请列表。仅 homepage `is_pop=true` 时返回并清服务端队列；
   * `false`/不传不返回、不清队列。
   */
  new_pending_invites?: HeartVoyagePendingInviteItem[]
}

/** 可邀请用户 HeartVoyageInviteUserItem */
export interface HeartVoyageInviteUserItem {
  member: ActivityMemberV2 | null
  intimacy: number
  show_intimacy: boolean
  invite_status: InviteStatus
  from_hot_room: boolean
}

/** 可邀请列表 data（heart_voyage_invite_list） */
export interface InviteListData {
  err_msg: string
  list: HeartVoyageInviteUserItem[]
}

/** 发起邀请 data（heart_voyage_invite_send） */
export interface InviteSendData {
  err_msg: string
  invite_id: string
}

/** 待处理邀请列表 data（heart_voyage_invite_pending） */
export interface InvitePendingData {
  err_msg: string
  pending: HeartVoyagePendingInviteItem[]
}

/** 处理邀请 data（heart_voyage_invite_handle） */
export interface InviteHandleData {
  err_msg: string
  pair_key: string
}

/** 当前查看关系 HeartVoyageRelationItem（api README §2.3） */
export interface HeartVoyageRelationItem {
  pair_key: string
  relation_source: number
  relation_type: RelationType
  peer: ActivityMemberV2 | null
  affinity: number
  ship_level: number
  ship_form: number
}

/** 解除限时关系 data（heart_voyage_unbind） */
export interface UnbindData {
  err_msg: string
}

/** 批量关系查询 data（heart_voyage_batch_binding_info） */
export interface HeartVoyageBatchBindingInfoData {
  err_msg: string
  /** key 为字符串化 mid，value 为对方 mid 列表；未绑为空数组 */
  bind_map: Record<string, number[]>
}

/** 关系列表 data（heart_voyage_relation_list） */
export interface RelationListData {
  err_msg: string
  stage: number
  has_limit_relation: boolean
  show_switch_relation: boolean
  /** 本人资料；无关系时仍返回 */
  self: ActivityMemberV2 | null
  list: HeartVoyageRelationItem[]
}

/** 本抽屉消费的首页字段（heart_voyage_homepage） */
export interface HeartVoyageHomepageBindFields {
  err_msg: string
  /** true = 需要新手引导 */
  need_newbie_guide: boolean
  /** 仅 `is_pop=true` 时返回 */
  new_pending_invites?: HeartVoyagePendingInviteItem[]
}

/** MightValueIntro 消费的首页字段（heart_voyage_homepage） */
export interface HeartVoyageHomepageMightFields {
  err_msg: string
  normal_gift_rate: number
  special_gift_rate: number
  special_affinity_rate: number
  wheel_game_url: string
  gold_miner_game_url: string
  jump_blind_box_gift_id: number
  current_relation: HeartVoyageRelationItem | null
}

/**
 * 首页头图相关字段（heart_voyage_homepage，不进进页弹层队列）
 * 头图态只看 header_pair 是否为空；stage 随包保留
 */
export interface HomepageHeroFlags {
  countdown: number
  stage: number
  header_pair: HeartVoyageRankPairItem | null
}

/** homepage Mock 聚合（多 task 共用同一 HeartVoyageHomepageMock） */
export type HeartVoyageHomepageMockData = HeartVoyageHomepageBindFields &
  HeartVoyageHomepageMightFields &
  HomepageHeroFlags &
  HeartVoyageHomepageVoyageFields &
  HeartVoyageHomepageRulesFields &
  HeartVoyageHomepageRewardsFields &
  HeartVoyageHomepagePeakFields

/** @see HeartVoyageHomepageMockData */
export type HeartVoyageHomepageData = HeartVoyageHomepageMockData

/** 进房目标 data（heart_voyage_jump_room） */
export interface JumpRoomData {
  err_msg: string
  room_id: number
}

/** 阶段奖励操作 action_status（api README §2.3） */
export enum StageActionStatus {
  Locked = 0,
  Claimable = 1,
  RemindPeer = 2,
}

/** 展示用奖励 TaskReward */
export interface TaskReward {
  id: number
  icon: string
  desc: string
  privilege_id: number | null
  has_show_effect: boolean
  quantity: number
  reward_type: number
}

/** 领取结果 SimpleRewardInfo（与 TaskReward 字段不同，禁止混用） */
export interface SimpleRewardInfo {
  id: number
  name: string
  icon: string
  desc: string
  value: number
  num: number
  reward_type: number
}

/** 领取阶段奖励 data（heart_voyage_stage_claim） */
export interface HeartVoyageStageClaimData {
  err_msg: string
  rewards: SimpleRewardInfo[]
}

/** 提醒对方领奖 data（heart_voyage_stage_remind） */
export interface HeartVoyageStageRemindData {
  err_msg: string
}

/** 奖励页子标题块 HeartVoyageShowSubtitle（homepage.ship/daily/peak_*_rewards） */
export interface HeartVoyageShowSubtitle {
  subtitle: string
  special_rewards: TaskReward[]
  normal_rewards: TaskReward[]
}

/** 奖励页展示块 HeartVoyageShowRewardBlock */
export interface HeartVoyageShowRewardBlock {
  display_order: number
  title: string
  subtitles: HeartVoyageShowSubtitle[]
}

/** RewardsPage 消费的首页字段（heart_voyage_homepage） */
export interface HeartVoyageHomepageRewardsFields {
  activity_id: number
  ship_rewards: HeartVoyageShowRewardBlock[]
  daily_rewards: HeartVoyageShowRewardBlock[]
  peak_audition_rewards: HeartVoyageShowRewardBlock[]
  peak_final_rewards: HeartVoyageShowRewardBlock[]
}

/** 展示形态阶段 HeartVoyageFormStageItem */
export interface HeartVoyageFormStageItem {
  level: number
  form_id: number
  total_affinity?: number
  rewards: TaskReward[]
}

/** 船只阶段详情 data（heart_voyage_stage_info） */
export interface HeartVoyageStageInfoData {
  err_msg: string
  affinity: number
  ship_level: number
  ship_form: number
  cur_progress: number
  need_affinity: number
  form_stages: HeartVoyageFormStageItem[]
  loop_mode: boolean
  action_status: number
  next_level_rewards: TaskReward[]
  unlock_all_forms: boolean
}

/** 每日挑战组内关系对 HeartVoyageDailyMemberItem */
export interface HeartVoyageDailyMemberItem {
  pair_key: string
  affinity: number
  is_self: boolean
  ship_level: number
  ship_form: number
  rel_type: RelationType
  member_a: ActivityMemberV2 | null
  member_b: ActivityMemberV2 | null
}

/** 每日挑战轮次 HeartVoyageDailyRoundItem */
export interface HeartVoyageDailyRoundItem {
  round: number
  event_time: string
  /** 轮次结束时刻（大区本地 `HH:mm`）；与 `event_time` 组成 Tab 第二行 */
  end_time: string
  pass_rewards: TaskReward[]
  top1_rewards: TaskReward[]
  round_status: number
}

/** 每日挑战详情 data（heart_voyage_daily_info） */
export interface HeartVoyageDailyInfoData {
  err_msg: string
  date: string
  round: number
  group_id: number
  pair_affinity: number
  threshold: number
  members: HeartVoyageDailyMemberItem[]
  round_status: number
  can_join: boolean
  eliminated: boolean
  pre_eliminate_index: number
  rounds: HeartVoyageDailyRoundItem[]
  round_countdown: number
}

/** 全区日榜 data（heart_voyage_daily_area_rank） */
export interface HeartVoyageDailyAreaRankData {
  err_msg: string
  date: string
  list: HeartVoyageRankPairItem[]
  own: HeartVoyageRankPairItem | null
  day_countdown: number
}

/** VoyageContent 消费的首页字段（heart_voyage_homepage） */
export interface HeartVoyageHomepageVoyageFields {
  pair_key: string
  show_switch_relation: boolean
  bind_rewards: TaskReward[]
  /** 本人资料；无关系时仍返回 */
  self: ActivityMemberV2 | null
}

/** RulesPage 消费的首页字段（heart_voyage_homepage） */
export interface HeartVoyageHomepageRulesFields {
  audition_start_time: string
  audition_end_time: string
  final_start_time: string
  final_end_time: string
  /** 循环门槛递增量；超最高级后每级单级增量固定为此值 */
  max_level_add: number
  /** 展示形态阶段；规则页船组横滑展示 level / form_id */
  form_stages: HeartVoyageFormStageItem[]
}

/** 阶段任务轮播块 HeartVoyageStageBannerBlock */
export interface HeartVoyageStageBannerBlock {
  member_a: ActivityMemberV2 | null
  member_b: ActivityMemberV2 | null
  rel_type: RelationType
  ship_level: number
  affinity: number
  need_affinity: number
}

/** 每日赛事轮播块 HeartVoyageDailyBannerBlock */
export interface HeartVoyageDailyBannerBlock {
  member_a: ActivityMemberV2 | null
  member_b: ActivityMemberV2 | null
  rel_type: RelationType
  round: number
  pair_affinity: number
  threshold: number
  round_countdown: number
}

/** 巅峰赛轮播块 HeartVoyagePeakBannerBlock */
export interface HeartVoyagePeakBannerBlock {
  member_a: ActivityMemberV2 | null
  member_b: ActivityMemberV2 | null
  rel_type: RelationType
  segment: number
  group_id: number
  group_name: string
  rank: number
  score: number
  score_gap: number
  segment_countdown: number
}

/** 回顾期 TOP HeartVoyageReviewTopBannerBlock */
export interface HeartVoyageReviewTopBannerBlock {
  member_a: ActivityMemberV2 | null
  member_b: ActivityMemberV2 | null
  rel_type: RelationType
  top_rank: number
}

/** 房间大挂件解析后 payload（data.pendant["heart_voyage"]） */
export interface HeartVoyagePendantPayload {
  activity_name: string
  activity_id: number
  url: string
  weight: number
  stage: number
  has_relation: boolean
  daily_can_join: boolean
  stage_banner: HeartVoyageStageBannerBlock
  daily_banner: HeartVoyageDailyBannerBlock | null
  peak_banner: HeartVoyagePeakBannerBlock | null
  review_tops: HeartVoyageReviewTopBannerBlock[]
  segment_name: string
  start_time_text: string
  coin_reward: number
  coin_reward_icon: string
  guide_text: string
  self_member: ActivityMemberV2 | null
  has_empty_slot: boolean
}

/** 概率礼物榜单项（prob_gift_act_rank list） */
export interface ProbGiftActRankItem {
  rank: number
  mid: number
  name: string
  avatar: string
  score: number
  estimate_coins: number
  gap_to_top3?: number
}

/** 概率礼物榜单本人（prob_gift_act_rank mine） */
export interface ProbGiftActRankMine extends ProbGiftActRankItem {
  gap_to_top3: number
}

/** 概率礼物榜单 data（prob_gift_act_rank） */
export interface ProbGiftActRankData {
  err_msg: string
  list: ProbGiftActRankItem[]
  mine: ProbGiftActRankMine
  top: number
  offset: number
  limit: number
  more: boolean
}

/** 概率礼物首页 data（prob_gift_act_home） */
export interface ProbGiftActHomeData {
  err_msg: string
  phase: number
  countdown_sec: number
  prize_pool: number
  my_score: number
  my_rank: number
  rank_display: string
  gap_to_top3: number
  estimate_coins: number
  estimate_ratio: number
  divide_ratio_text: string
  score_per_coin: number
  can_join: boolean
  play: number[]
  config_id: number
  warmup_st: number
  act_st: number
  act_et: number
  review_et: number
  b_gift_id: number
  room_id: number
  score_tasks: unknown[]
}

/** 礼物面板 Banner data（heart_voyage_gift_banner） */
export interface HeartVoyageGiftBannerData {
  err_msg: string
  show: boolean
  activity_id: number
  url: string
  has_relation: boolean
  pair_key: string
  /** 每日赛事是否可参与；淘汰或无分组为 false，未绑为 false */
  daily_can_join: boolean
  /** 本次默契增量；HTTP 快照恒为 0，长连 419 为实际增量 */
  add_affinity: number
  /** 本次赛段榜加分；HTTP 快照恒为 0，长连 419 为实际增量 */
  add_score: number
  /** 本次每日窗口默契增量；HTTP 快照恒为 0，长连 419 为实际增量 */
  add_pair_affinity: number
  self_member: ActivityMemberV2 | null
  guide_text: string
  has_empty_slot: boolean
  coin_reward: number
  coin_reward_icon: string
  stage_banner: HeartVoyageStageBannerBlock
  peak_banner: HeartVoyagePeakBannerBlock
  /** 每日赛事 Banner；仅 daily_can_join=true 时为对象，否则 null */
  daily_banner: HeartVoyageDailyBannerBlock | null
}
